'use client';
import { forwardRef, type MouseEvent, type ReactNode, useImperativeHandle, useRef } from 'react';
import { ACCEPT_IMAGE_MIMETYPE, MAX_IMAGE_FILE_SIZE, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useFileUpload, useImageValidator } from '@/app/hooks';
import type { GetSelectedImageResult, ImageUploadFile, ImageUploadFileInfo } from '@/app/types';
import { hysdk } from '@/app/utils/client';

interface ImageResolutionValidationResult {
  errorMessage: string;
  errorReason: string;
}

export interface ImageUploaderRenderApi {
  openFileSelector: (e?: MouseEvent) => Promise<void>;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
}

export interface HeadlessImageUploaderProps {
  /** 最大允许上传的图片数量 */
  maxCount?: number;
  disabled?: boolean;
  /** 上传图片的最大大小 */
  maxFileSize?: number;
  /** 是否给 selectedImageRange 初始值 */
  isInitialSelectedImageRange?: boolean;
  /** 上传场景，社区用 'community'，主体用 'material'，默认为 'vidu' */
  scene?: UploadScene;
  onChange?: (file: ImageUploadFile, fileUploadInfo?: ImageUploadFileInfo) => void;
  /** 获取图片（如裁剪区域） */
  getSelectedImage?: (url: string) => Promise<GetSelectedImageResult>;
  /**
   * 选完图后跳过默认「立即上传」流程的逃生口。
   *
   * 设置后：picker 选完文件只触发本回调，**不**走 processFileUpload / 不发 onChange；
   * 调用方负责所有后续动作（本地校验 / 弹裁剪 / 决定何时上传）。
   *
   * 适用于「选图 → 本地裁剪 → 只上传裁剪结果」这类需要规避一次性上传原图的场景。
   */
  onPickFiles?: (files: File[]) => void | Promise<void>;
  /** 是否允许临时模式（未登录时不上传，只保存临时图片） */
  allowTempMode?: boolean;
  /** 临时模式下是否仍然先发出 uploading 事件 */
  emitUploadingInTempMode?: boolean;
  /** 图片处理出本地预览后，上传完成前再发一次带 file 的 uploading 事件 */
  emitUploadingPreview?: boolean;
  validateImageResolution?: (imageResolution: {
    width: number;
    height: number;
  }) => ImageResolutionValidationResult | null;
  /** 支持的图片格式 */
  imageAccept?: string[];
  /**
   * 上传失败（AuditSubmitIllegal）时全局 confirmDialog 的视觉主题。
   * 不传/'default' = 全站默认；'admission' = h5/thu-2026-freshmen 紫色风。
   */
  dialogTheme?: 'default' | 'admission';
}

/**
 * Headless 图片上传组件
 *
 * 只处理隐藏的 input 与选择/上传逻辑，不包含拖拽区或任何默认 UI。
 * 适用于需要在更大区域实现自定义拖拽、或完全自定义布局的场景。
 *
 * @example
 * ```tsx
 * <HeadlessImageUploader
 *   onChange={handleChange}
 *   getSelectedImage={getSelectedImage}
 * >
 *   {({ openFileSelector, uploadFiles }) => (
 *     <button type="button" onClick={openFileSelector}>选择图片</button>
 *   )}
 * </HeadlessImageUploader>
 * ```
 */
export const HeadlessImageUploader = forwardRef<
  HTMLInputElement,
  HeadlessImageUploaderProps & {
    children?: (api: ImageUploaderRenderApi) => ReactNode;
  }
>(
  (
    {
      maxCount = 1,
      disabled,
      maxFileSize = MAX_IMAGE_FILE_SIZE,
      isInitialSelectedImageRange = true,
      scene = UPLOAD_SCENE.VIDU,
      getSelectedImage,
      onChange,
      onPickFiles,
      allowTempMode = false,
      emitUploadingInTempMode = true,
      emitUploadingPreview = false,
      validateImageResolution,
      imageAccept = ACCEPT_IMAGE_MIMETYPE,
      dialogTheme,
      children,
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => fileInputRef.current as HTMLInputElement, []); //合并ref

    const { validateImage } = useImageValidator({
      maxFileSize,
      acceptedMimeTypes: imageAccept,
      validateResolution: validateImageResolution,
    }); // 处理图片验证

    // 处理图片上传
    const { processFileUpload } = useFileUpload({
      onChange: onChange ?? (() => {}),
      getSelectedImage: getSelectedImage ?? (async () => null),
      validateImage,
      isInitialSelectedImageRange,
      scene,
      allowTempMode,
      emitUploadingInTempMode,
      emitUploadingPreview,
      dialogTheme,
    });

    // 处理文件上传：单文件、多文件、拖拽
    const handleFiles = async (files: FileList | File[]) => {
      let fileArray = Array.from(files);
      // 如果文件数量超过限制，则只上传前 maxCount 个文件
      if (fileArray.length > maxCount) {
        fileArray = fileArray.slice(0, maxCount);
      }
      // 逃生口：调用方接管，跳过默认 processFileUpload。
      if (onPickFiles) {
        await onPickFiles(fileArray);
        return;
      }
      await Promise.all(fileArray.map((file, index) => processFileUpload(file, index)));
    };

    // 处理文件选择器
    const handleOpenFileSelector = async (e?: React.MouseEvent) => {
      if (disabled) return;
      // 检查iOS权限，如果没有权限则不打开文件选择器
      if (hysdk.sniff.isViduApp && hysdk.sniff.os === 'ios') {
        const hasPermission = await hysdk.getAppPermissionInfo(['album', 'camera']);
        if (!hasPermission) {
          e?.stopPropagation();
          return;
        }
      }
      // 手动触发文件选择器
      if (fileInputRef.current) fileInputRef.current.click();
    };

    return (
      <>
        {children?.({ openFileSelector: handleOpenFileSelector, uploadFiles: handleFiles })}
        <input
          hidden
          accept={imageAccept.join(',')}
          type="file"
          ref={fileInputRef}
          multiple={maxCount > 1}
          disabled={disabled}
          onChange={async (e) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;
            await handleFiles(files);
            e.target.value = ''; // 清空文件输入
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </>
    );
  },
);

HeadlessImageUploader.displayName = 'HeadlessImageUploader';
